import orderBy from 'lodash/orderBy'

import api from 'api'
import {
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_MASTER_ROUTE_BRANCH_LIST,
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_STOPS_RECONCILE,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTE_STOP_LISTS,
} from 'api/cacheTagTypes'

import type { MasterStopApi } from 'types/api/route/masterStop'
import type {
  ApiDayOfWeek,
  AssignMasterStopsBody,
  AssignMasterStopsResponse,
  ChangeDayOfWeekBody,
  GetStopDetailsBody,
  MasterBranchBody,
  RestoreMasterVersionBody,
  RestoreMasterVersionPreviewResponse,
  RestoreMasterVersionResponse,
  StopSequence,
  SyncDailyToMasterBody,
  SyncDailyToMasterResponse,
  UnassignMasterStopsBody,
  UpdateMasterSequenceBody,
} from 'types/api/route/masterStopRequests'

// Daily versions tag is included because master publish propagates to dailies; the daily activity
// feed must refresh alongside master. Mirrors `getInvalidationScope('master-publish')` in v2.
const LIVE_PUBLISH_INVALIDATION_TAGS = [
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
] as const

interface RouteIdArg {
  routeId: string
}

interface SkipInvalidateArg {
  skipInvalidate?: boolean
}

interface GetMasterRouteMetaArgs extends RouteIdArg {
  dayOfWeek: ApiDayOfWeek
  includeUnassignedCount?: boolean
}

interface GetMasterStopsArgs extends RouteIdArg {
  dayOfWeek: ApiDayOfWeek
  includeTags?: boolean
  includeContainers?: boolean
}

interface GetMasterStopsDetailsArgs extends RouteIdArg {
  includeTags?: boolean
  includeContainers?: boolean
  data: GetStopDetailsBody
}

interface GetMasterStopsGeojsonArgs extends RouteIdArg {
  dayOfWeek: ApiDayOfWeek
}

interface UpdateMasterSequenceArgs extends RouteIdArg, SkipInvalidateArg {
  data: UpdateMasterSequenceBody
}

interface UnassignMasterStopsArgs extends RouteIdArg, SkipInvalidateArg {
  data: UnassignMasterStopsBody
}

interface AssignMasterStopsArgs extends RouteIdArg, SkipInvalidateArg {
  data: AssignMasterStopsBody
}

interface ChangeDayOfWeekMasterStopsArgs extends RouteIdArg {
  data: ChangeDayOfWeekBody
}

interface GetMasterStopsVersionsArgs extends RouteIdArg {
  dayOfWeek: ApiDayOfWeek
}

interface GetMasterBranchListArgs extends RouteIdArg {
  dayOfWeek: ApiDayOfWeek
  includeArchived?: boolean
}

interface GetMasterBranchDetailsArgs extends RouteIdArg {
  branchId: string
}

interface CreateMasterBranchArgs extends RouteIdArg {
  data: MasterBranchBody
}

interface UpdateMasterBranchArgs extends RouteIdArg, SkipInvalidateArg {
  branchId: string
  data: MasterBranchBody
}

interface RestoreMasterVersionArgs extends RouteIdArg, SkipInvalidateArg {
  data: RestoreMasterVersionBody
}

interface SyncDailyToMasterArgs extends RouteIdArg, SkipInvalidateArg {
  data: SyncDailyToMasterBody
}

export interface MasterStopsResponse {
  stops: MasterStopApi[]
}

export interface MasterStopsVersionsResponse {
  versions: ReadonlyArray<{
    id: string
    rev: number
    createdBy: string
    createdAt: string
    createdByUser: unknown
    sequence: StopSequence[]
  }>
}

export interface MasterStopsVersionsLatestResponse {
  master: number | null
  daily: Record<string, number>
}

// Attribution-only payload for the `/versions/latest/details` endpoint; `sequence` omitted BE-side.
export interface VersionDetailsEntry {
  id: string
  rev: number
  createdBy: string
  createdAt: string
  createdByUser: unknown
}

const extendedApi = api.injectEndpoints({
  endpoints: builder => ({
    getMasterRouteMeta: builder.query<unknown, GetMasterRouteMetaArgs>({
      query: ({ routeId, dayOfWeek, includeUnassignedCount = false }) => ({
        url: `/api/v2/route/${routeId}/master/stops/meta`,
        method: 'GET',
        params: { dayOfWeek, includeUnassignedCount },
      }),
      providesTags: [CACHE_TAG_MASTER_ROUTE_META],
    }),
    getMasterStops: builder.query<MasterStopsResponse, GetMasterStopsArgs>({
      query: ({ routeId, dayOfWeek, includeTags = true, includeContainers = false }) => ({
        url: `/api/v2/route/${routeId}/master/stops`,
        method: 'GET',
        params: { dayOfWeek, includeTags, includeContainers },
      }),
      // Stop payloads run to megabytes on large routes and are replaced wholesale;
      // deep-diffing them for referential reuse costs more than it saves.
      structuralSharing: false,
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    // Same endpoint as `getMasterStops`, but parked under its own cache
    // tag so it can refresh independently of the primary stops cache.
    // Mutations that invalidate `CACHE_TAG_ROUTES_STOPS` (assign / unassign
    // / publish) deliberately leave this slot alone — that's what makes it
    // safe to poll in the background while the user has in-flight preview /
    // restore / move state on the grid: their working copy isn't refetched
    // out from under them, but we still see fresh BE metadata that consumers
    // can keyBy(configuredServiceEventId) and overlay onto cards (note
    // counts, tags, materials, address edits, etc.).
    getMasterStopsReconcile: builder.query<MasterStopsResponse, GetMasterStopsArgs>({
      query: ({ routeId, dayOfWeek, includeTags = true, includeContainers = false }) => ({
        url: `/api/v2/route/${routeId}/master/stops`,
        method: 'GET',
        params: { dayOfWeek, includeTags, includeContainers },
      }),
      structuralSharing: false,
      providesTags: [CACHE_TAG_MASTER_ROUTE_STOPS_RECONCILE],
    }),
    getMasterStopsDetails: builder.query<MasterStopsResponse, GetMasterStopsDetailsArgs>({
      query: ({ routeId, includeTags = true, includeContainers = false, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/get/details`,
        method: 'POST',
        params: { includeTags, includeContainers },
        body: data,
      }),
      providesTags: [CACHE_TAG_ROUTES_STOPS, CACHE_TAG_ROUTE_STOP_LISTS],
    }),
    getMasterStopsGeojson: builder.query<unknown, GetMasterStopsGeojsonArgs>({
      query: ({ routeId, dayOfWeek }) => ({
        url: `/api/v2/route/${routeId}/master/stops/geojson`,
        method: 'GET',
        params: { dayOfWeek },
      }),
      providesTags: [CACHE_TAG_ROUTES_GEOJSON],
    }),
    updateMasterSequence: builder.mutation<void, UpdateMasterSequenceArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/sequence`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [...LIVE_PUBLISH_INVALIDATION_TAGS]),
    }),
    unassignMasterStops: builder.mutation<void, UnassignMasterStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/unassign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [...LIVE_PUBLISH_INVALIDATION_TAGS]),
    }),
    assignMasterStops: builder.mutation<AssignMasterStopsResponse, AssignMasterStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/assign`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [...LIVE_PUBLISH_INVALIDATION_TAGS]),
    }),
    changeDayOfWeekMasterStops: builder.mutation<void, ChangeDayOfWeekMasterStopsArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/day-of-week`,
        method: 'POST',
        body: data,
      }),
      // Reached only through the route-operation pipeline, whose tail step invalidates for the whole change.
      invalidatesTags: [],
    }),
    getMasterStopsVersions: builder.query<MasterStopsVersionsResponse, GetMasterStopsVersionsArgs>({
      query: ({ routeId, dayOfWeek }) => ({
        url: `/api/v2/route/${routeId}/master/stops/versions`,
        method: 'GET',
        params: { dayOfWeek },
      }),
      // Order by (rev DESC, createdAt DESC) so the activity panel renders newest first
      // and same-rev rows are tie-broken deterministically by createdAt. Sorting here
      // (instead of in the consuming component) means every consumer of this query
      // gets the canonical order automatically.
      transformResponse: (response: MasterStopsVersionsResponse) => ({
        ...response,
        versions: orderBy(response.versions, ['rev', 'createdAt'], ['desc', 'desc']),
      }),
      providesTags: [CACHE_TAG_MASTER_ROUTE_VERSIONS],
    }),
    getMasterStopsVersionsLatest: builder.query<MasterStopsVersionsLatestResponse, GetMasterStopsVersionsArgs>({
      query: ({ routeId, dayOfWeek }) => ({
        url: `/api/v2/route/${routeId}/master/stops/versions/latest`,
        method: 'GET',
        params: { dayOfWeek },
      }),
      providesTags: [CACHE_TAG_MASTER_ROUTE_VERSIONS, CACHE_TAG_DAILY_ROUTE_VERSIONS],
    }),
    getMasterStopsVersionsLatestDetails: builder.query<VersionDetailsEntry | null, GetMasterStopsVersionsArgs>({
      query: ({ routeId, dayOfWeek }) => ({
        url: `/api/v2/route/${routeId}/master/stops/versions/latest/details`,
        method: 'GET',
        params: { dayOfWeek },
      }),
      providesTags: [CACHE_TAG_MASTER_ROUTE_VERSIONS],
    }),
    getMasterBranchList: builder.query<unknown, GetMasterBranchListArgs>({
      query: ({ routeId, dayOfWeek, includeArchived = false }) => ({
        url: `/api/v2/route/${routeId}/master/branch`,
        method: 'GET',
        params: { dayOfWeek, includeArchived },
      }),
      providesTags: [CACHE_TAG_MASTER_ROUTE_BRANCH_LIST],
    }),
    getMasterBranchDetails: builder.query<unknown, GetMasterBranchDetailsArgs>({
      query: ({ routeId, branchId }) => ({
        url: `/api/v2/route/${routeId}/master/branch/${branchId}`,
        method: 'GET',
      }),
    }),
    createMasterBranch: builder.mutation<unknown, CreateMasterBranchArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/branch`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [CACHE_TAG_MASTER_ROUTE_BRANCH_LIST],
    }),
    updateMasterBranch: builder.mutation<unknown, UpdateMasterBranchArgs>({
      query: ({ routeId, branchId, data }) => ({
        url: `/api/v2/route/${routeId}/master/branch/${branchId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [CACHE_TAG_MASTER_ROUTE_BRANCH_LIST]),
    }),
    restoreMasterVersion: builder.mutation<RestoreMasterVersionResponse, RestoreMasterVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/restore`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [...LIVE_PUBLISH_INVALIDATION_TAGS]),
    }),
    // Preview is read-only — same body as the committing mutation, no cache
    // invalidation. Caller joins the returned IDs against master/daily
    // details to render cards (see `useRestoreVersionPreview`).
    previewRestoreMasterVersion: builder.mutation<RestoreMasterVersionPreviewResponse, RestoreMasterVersionArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/restore/preview`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [],
    }),
    syncDailyToMaster: builder.mutation<SyncDailyToMasterResponse, SyncDailyToMasterArgs>({
      query: ({ routeId, data }) => ({
        url: `/api/v2/route/${routeId}/master/stops/sync-daily`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, arg) => (arg?.skipInvalidate ? [] : [...LIVE_PUBLISH_INVALIDATION_TAGS]),
    }),
  }),
})

export const {
  useLazyGetMasterRouteMetaQuery,
  useLazyGetMasterStopsQuery,
  useGetMasterStopsReconcileQuery,
  useLazyGetMasterStopsReconcileQuery,
  useLazyGetMasterStopsDetailsQuery,
  useLazyGetMasterStopsGeojsonQuery,
  useUpdateMasterSequenceMutation,
  useUnassignMasterStopsMutation,
  useAssignMasterStopsMutation,
  useChangeDayOfWeekMasterStopsMutation,
  useLazyGetMasterStopsVersionsQuery,
  useLazyGetMasterStopsVersionsLatestQuery,
  useLazyGetMasterStopsVersionsLatestDetailsQuery,
  useLazyGetMasterBranchListQuery,
  useLazyGetMasterBranchDetailsQuery,
  useCreateMasterBranchMutation,
  useUpdateMasterBranchMutation,
  useRestoreMasterVersionMutation,
  usePreviewRestoreMasterVersionMutation,
  useSyncDailyToMasterMutation,
} = extendedApi
